import { Check } from 'lucide-react';
import type { ReactElement } from 'react';

import { usePermission } from '@/hooks/usePermission';

import type { DossierSeal } from '../types';

import { SealVerification } from './SealVerification';
import { formatDateTime } from '@/lib/time';

const nameOf = (n: string | null): string => n ?? 'Usuario dado de baja';

/** G2 (A-04): quiénes firmaron la aprobación, "Ana Solano y Walter Rojas". */
function signerNames(seal: NonNullable<DossierSeal>): string {
  const names = (seal.signers.length > 0 ? seal.signers : [seal]).map((s) =>
    nameOf(s.signer.displayName),
  );
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} y ${names[names.length - 1] ?? ''}`;
}

/** Sello de firma electrónica de la versión efectiva. `null` = documento sin firmar
 *  (estado honesto, no un sello en blanco). */
export function SignatureSeal({ seal }: { seal: DossierSeal }): ReactElement {
  // Verifica quien aprueba o quien lee la Lista Maestra (spec 2026-10-14 §6 D2); un
  // proveedor con acceso temporal ve el sello, pero no lo verifica.
  const canApprove = usePermission('quality:document:approve');
  const canRead = usePermission('quality:master-list:read');
  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Sello de firma electrónica</h3>
        <span className="count">firma de aprobación</span>
      </div>
      <div className="cb">
        {seal === null ? (
          <p className="soft" style={{ fontSize: 13 }}>
            Sin firmar. La versión efectiva de este documento todavía no tiene firma de aprobación.
          </p>
        ) : (
          <>
            <div className="seal">
              <div className="ck">
                <Check aria-hidden="true" />
              </div>
              <div className="st">
                <b>Firmado y bloqueado · {signerNames(seal)}</b>
                {formatDateTime(seal.signedAt)} · la edición de esta versión quedó bloqueada
              </div>
              <div className="lock">Sellado</div>
            </div>
            {seal.signers.length > 1 ? (
              <ul className="seal-signers">
                {seal.signers.map((s) => (
                  <li key={s.signatureId}>
                    {nameOf(s.signer.displayName)}, {formatDateTime(s.signedAt)}
                  </li>
                ))}
              </ul>
            ) : null}
            {seal.voided.length > 0 ? (
              <div className="seal-voided">
                <p className="act-note strong">Sin efecto por devolución</p>
                {seal.voided.map((s) => (
                  <p key={s.signatureId} className="act-note">
                    {nameOf(s.signer.displayName)}, {formatDateTime(s.signedAt)}: sin efecto
                  </p>
                ))}
              </div>
            ) : null}
            {canApprove || canRead ? (
              <SealVerification key={seal.signatureId} signatureId={seal.signatureId} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
